import { useRef, useEffect } from 'react'
import LogoHorizontal from '@/icons/logo-horizontal.svg'
import Container from '@/components/container'
import LinkComponent from '@/components/link-component'
import SelectLocale from '@/components/select-locale'
import CtaLink from '@/components/ui/cta-link'
import RichText from '@/components/ui/rich-text'
import { cx } from '@/utils/strings'
import routes from '@/constants/routes'
import Newsletter from './newsletter'

import ThemeSwitcher from './ui/theme-switcher'

export default function Footer({ footerNav, newsletterForm, showLinkSections = true, otherLocales = [] }) {
  if (!footerNav) return <></>
  const footerBottomLinksRef = useRef(null)

  useEffect(() => {
    const consentLink =
      footerBottomLinksRef?.current?.querySelector('a[href$="#consent"]') ||
      footerBottomLinksRef?.current?.querySelector('a[href$="#osano"]')

    if (consentLink) {
      consentLink.addEventListener('click', (event) => {
        if (window.ketch) {
          event.preventDefault()
          window.ketch('showPreferences')
        }
      })
    }
  }, [])

  const {
    fields: { linkSections, bottomText },
  } = footerNav

  const footerIconStyle = 'text-base '
  const footerLinkStyle =
    'text-base text-secondary-800 hover:text-primaryBlue dark:text-secondary-200 dark:hover:text-secondary-300 [&_span.font-bwi]:text-[1.2rem] xl:[&_span.font-bwi]:text-[1.4rem]'

  const renderBottomLinks = () => (
    <div
      ref={footerBottomLinksRef}
      className="text-center lg:text-left [&_p]:text-base [&_p]:mb-0 mb-0 [&>p>a]:no-underline [&>p>a]:ml-1 [&>p>a]:text-secondary-800 [&>p>a:hover]:text-primaryBlue dark:[&>p>a]:text-secondary-200 dark:[&>p>a:hover]:text-secondary-300"
    >
      <RichText body={bottomText.fields.body} />
    </div>
  )

  return (
    <footer className="bg-secondary-50 py-10 border-t border-secondary-100 dark:bg-secondary-900 dark:border-secondary-900 relative">
      <Container>
        {showLinkSections ? (
          <div className="flex flex-col gap-10">
            <nav className="grid gap-8 sm:grid-cols-2 md:grid-cols-4">
              {linkSections.map((section) => (
                <div key={`section-${section.sys?.id || section.fields.heading}`}>
                  <h4 className="text-2xl mb-4">{section.fields.heading}</h4>
                  <ul className="flex flex-col gap-3 list-none">
                    {section.fields.links.map((link, i) => (
                      <li key={link.sys.id}>
                        <CtaLink
                          to={link.fields.link.fields.url}
                          className={cx(footerLinkStyle)}
                          size="Smaller"
                          style="Text Link"
                        >
                          {link.fields.label}
                        </CtaLink>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
            {newsletterForm && <Newsletter form={newsletterForm.fields} />}
            <hr className="dark:border-secondary-500" />
            <div className="flex flex-col max-lg:gap-6 gap-4 xl:gap-6 justify-between items-center lg:flex-row [&>p]:text-base">
              {renderBottomLinks()}
              <div className="flex gap-3 xl:gap-4 flex-wrap">
                <LinkComponent to="https://twitter.com/bitwarden" className={cx(footerIconStyle, footerLinkStyle)}>
                  <span className="font-bwi" data-name="bwi-twitter">
                    &#xe9a5;
                  </span>
                </LinkComponent>
                <LinkComponent to="https://reddit.com/r/bitwarden" className={cx(footerIconStyle, footerLinkStyle)}>
                  <span className="font-bwi" data-name="bwi-reddit">
                    &#xe95a;
                  </span>
                </LinkComponent>
                <LinkComponent to="https://community.bitwarden.com/" className={cx(footerIconStyle, footerLinkStyle)}>
                  <span className="font-bwi" data-name="bwi-community">
                    &#xe994;
                  </span>
                </LinkComponent>
                <LinkComponent to="https://github.com/bitwarden" className={cx(footerIconStyle, footerLinkStyle)}>
                  <span className="font-bwi" data-name="bwi-github">
                    &#xe950;
                  </span>
                </LinkComponent>
                <LinkComponent
                  to="https://www.youtube.com/channel/UCId9a_jQqvJre0_dE2lE_Rw"
                  className={cx(footerIconStyle, footerLinkStyle)}
                >
                  <span className="font-bwi" data-name="bwi-youtube">
                    &#xe966;
                  </span>
                </LinkComponent>
                <LinkComponent
                  to="https://www.linkedin.com/company/bitwarden1"
                  className={cx(footerIconStyle, footerLinkStyle)}
                >
                  <span className="font-bwi" data-name="bwi-linkedin">
                    &#xe955;
                  </span>
                </LinkComponent>
                <LinkComponent
                  to="https://www.facebook.com/bitwarden/"
                  className={cx(footerIconStyle, footerLinkStyle)}
                >
                  <span className="font-bwi" data-name="bwi-facebook">
                    &#xe94d;
                  </span>
                </LinkComponent>
                <LinkComponent
                  to="https://www.instagram.com/bitwarden/"
                  className={cx(footerIconStyle, footerLinkStyle)}
                >
                  <span className="font-bwi" data-name="bwi-instagram">
                    &#xe999;
                  </span>
                </LinkComponent>
                <LinkComponent
                  rel="me"
                  to="https://fosstodon.org/@bitwarden"
                  className={cx(footerIconStyle, footerLinkStyle)}
                >
                  <span className="font-bwi" data-name="bwi-mastodon">
                    &#xe995;
                  </span>
                </LinkComponent>
              </div>
              <div>
                <SelectLocale otherLocales={otherLocales} />
              </div>
              <ThemeSwitcher />
            </div>
          </div>
        ) : (
          <div className="flex justify-between">
            <div className="w-40">
              <LinkComponent to={routes.index}>
                <LogoHorizontal className="text-primaryBlue dark:text-white" />
              </LinkComponent>
            </div>
            <div className="flex gap-4 items-center">
              {renderBottomLinks()}
              <ThemeSwitcher />
            </div>
          </div>
        )}
      </Container>
    </footer>
  )
}
